import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { EditorHeader } from "./EditorHeader";

export const MarketEditor = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: marketData, isLoading } = useQuery({
    queryKey: ['market-data'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('market_data')
        .select('*')
        .order('year');
      
      if (error) throw error;
      return data;
    },
  });

  const handleSave = async (e: React.FormEvent<HTMLFormElement>, dataId: string) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const updates = {
      year: Number(formData.get('year')),
      value: Number(formData.get('value')),
    };

    try {
      const { error } = await supabase
        .from('market_data')
        .update(updates)
        .eq('id', dataId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Market data has been updated",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update market data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    const currentYear = new Date().getFullYear();
    try {
      const { error } = await supabase
        .from('market_data')
        .insert({
          year: currentYear,
          value: 0,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "New market data point has been added",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add market data",
      });
    }
  };

  const handleDelete = async (dataId: string) => {
    try {
      const { error } = await supabase
        .from('market_data')
        .delete()
        .eq('id', dataId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Market data point has been deleted",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete market data",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditorHeader title="Market Data Editor" />
      <Button onClick={handleAdd} className="mb-4">
        <Plus className="h-4 w-4 mr-2" />
        Add Market Data Point
      </Button>

      <div className="space-y-6">
        {marketData?.map((data) => (
          <Card key={data.id}>
            <CardContent className="pt-6">
              <form onSubmit={(e) => handleSave(e, data.id)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Year</label>
                    <Input
                      name="year"
                      type="number"
                      defaultValue={data.year}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Value (Billions)</label>
                    <Input
                      name="value"
                      type="number"
                      step="0.01"
                      defaultValue={data.value}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex justify-between">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="destructive"
                    onClick={() => handleDelete(data.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};